import { CandidateProfile, ISalaryExpectation } from 'src/services/candidates/candidates.types';

import { RecordMetaItem } from 'src/components/record-panel/record-panel.types';

// ----------------------------------------------------------------------

type TFn = (key: string, options?: any) => string;

/** Compact salary range, e.g. `$80k–$100k / year`. Shared by the profile header and the record panel. */
export const formatSalary = (s: ISalaryExpectation, t: TFn): string => {
  const fmt = (n: number) => (n >= 1000 ? `${Math.round(n / 100) / 10}k` : `${n}`);
  const period = t(`enums.period_type.${s.period?.toLowerCase?.() ?? s.period}`, { defaultValue: '' });
  return `$${fmt(s.min_amount)}–$${fmt(s.max_amount)}${period ? ` / ${period}` : ''}`;
};

// ----------------------------------------------------------------------

/** One field that may appear on the candidate summary line (spec item 17, JT1-1538). */
export interface SummaryFieldDef {
  key: string;
  /** Translated label for the settings picker and the meta item. */
  label: (t: TFn) => string;
  /**
   * The rendered value for a profile, or null when there is nothing to show for this field.
   * `fallbackLocation` is the live/synced profile's location, used only by the location field: the
   * CRM/attached `location` is the source of truth and, when empty, the live value is shown read-only
   * (JT1-1535, item 31). The live profile is never written — this is display only.
   */
  getValue: (profile: CandidateProfile | undefined, t: TFn, fallbackLocation?: string) => string | null;
}

/** The `title` field is special: when selected it drives the header subtitle, not a labelled meta item. */
export const SUMMARY_TITLE_FIELD = 'title';

/**
 * The catalogue of fields an org can put on the candidate summary line, drawn from the same list-column
 * fields (JT1-1527/1528). Order here is only the picker's default order; the org's saved order wins.
 *
 * `title` is special-cased across the render sites: when selected it drives the header SUBTITLE — its
 * natural prominent slot from JT1-1537 — so buildCandidateSummaryMeta omits it from the labelled meta
 * row (see isSummaryTitleSelected). Every other field renders as a labelled item in the meta row.
 */
export const CANDIDATE_SUMMARY_FIELDS: SummaryFieldDef[] = [
  {
    key: SUMMARY_TITLE_FIELD,
    label: (t) => t('candidates.table.position_title', { defaultValue: 'Job title' }),
    getValue: (p) => p?.position_title || null,
  },
  {
    key: 'salary',
    label: (t) => t('candidates.table.salary_range', { defaultValue: 'Salary range' }),
    getValue: (p, t) => {
      const salary = p?.salary_expectations?.[0];
      return salary ? formatSalary(salary, t) : null;
    },
  },
  {
    key: 'location',
    label: (t) => t('candidates.table.location', { defaultValue: 'Location' }),
    // CRM location is the source of truth; fall back to the live/synced value read-only (JT1-1535).
    getValue: (p, _t, fallbackLocation) => p?.location || fallbackLocation || null,
  },
  {
    key: 'notice_period',
    label: (t) => t('candidates.table.notice_period', { defaultValue: 'Notice period' }),
    getValue: (p, t) =>
      p?.notice_period?.type
        ? t(`enums.notice_period_type.${p.notice_period.type}`, { defaultValue: p.notice_period.type })
        : null,
  },
  {
    key: 'email',
    label: (t) => t('candidates.table.email', { defaultValue: 'Email' }),
    getValue: (p) => p?.email || null,
  },
  {
    key: 'phone',
    label: (t) => t('candidates.table.phone', { defaultValue: 'Phone' }),
    getValue: (p) => (p?.phone_number ? `${p.country_code ?? ''} ${p.phone_number}`.trim() : null),
  },
];

/** Most fields the summary line may show (spec item 17). Kept in sync with the backend cap. */
export const MAX_SUMMARY_FIELDS = 4;

/** Seeded selection when an org has never customised the summary line (spec item 17). */
export const DEFAULT_SUMMARY_FIELDS: string[] = ['title', 'salary', 'location'];

const FIELD_BY_KEY = new Map(CANDIDATE_SUMMARY_FIELDS.map((f) => [f.key, f]));

/**
 * Resolve the org's saved field keys, falling back to the seeded default only while none have loaded
 * (undefined/null). An explicitly-saved empty selection is respected (the admin chose to show nothing).
 */
export const resolveSummaryFieldKeys = (keys: string[] | undefined | null): string[] =>
  keys ?? DEFAULT_SUMMARY_FIELDS;

/**
 * The candidate summary-line meta items shown under the name, in the org-selected order (spec item 17,
 * JT1-1538). Empty fields and unknown keys are dropped so the row never shows a blank label. `title` is
 * omitted here — it renders as the header subtitle (see isSummaryTitleSelected). When `keys` is omitted
 * the seeded default (salary + location, title being the subtitle) is used. `fallbackLocation` is the
 * live/synced location shown when the CRM location is empty (JT1-1535).
 */
export const buildCandidateSummaryMeta = (
  profile: CandidateProfile | undefined,
  t: TFn,
  keys?: string[] | null,
  fallbackLocation?: string,
): RecordMetaItem[] =>
  resolveSummaryFieldKeys(keys)
    .filter((key) => key !== SUMMARY_TITLE_FIELD) // title renders as the header subtitle, not a meta item
    .map((key): RecordMetaItem | null => {
      const field = FIELD_BY_KEY.get(key); // key no longer in the catalogue → dropped below
      const value = field?.getValue(profile, t, fallbackLocation);
      return field && value ? { key, label: field.label(t), value } : null;
    })
    .filter((item): item is RecordMetaItem => item !== null);

/** Whether the header should render the job-title subtitle (title is a selectable summary field). */
export const isSummaryTitleSelected = (keys?: string[] | null): boolean =>
  resolveSummaryFieldKeys(keys).includes(SUMMARY_TITLE_FIELD);
